import { createContext, useContext, useMemo } from 'react';
import getIn from 'transmute/getIn';
import { useLocation } from 'react-router-dom';
import { getIsIframePreviewUrl } from '../utils/urls';
import { useFetchDynamicSubtotalsPreview, useFetchDynamicSubtotals, useFetchDynamicSubtotalsWithTax } from '../hooks/useDynamicSubtotals';
import { useIframeableContext } from './IframeableContext';
import { usePaymentFormContext } from './PaymentFormContext';
import { jsx as _jsx } from "react/jsx-runtime";
const defaultState = {
  adjustmentAmounts: [],
  lineItems: [],
  loading: true,
  error: undefined,
  currencyCode: 'USD',
  oneTimeSubtotal: 0,
  oneTimeDiscountAmount: null,
  oneTimeSubtotalDiscountAmount: null,
  oneTimeSubtotalTaxAmount: null,
  firstPaymentSubtotal: 0,
  firstPaymentSubtotalBeforeTax: 0,
  firstPaymentTaxAmount: 0,
  recurringSubtotals: [],
  firstPaymentTotal: 0,
  firstPaymentTotalInSmallestCurrencyUnit: 0,
  paymentAmountInSmallestCurrencyUnit: 0,
  discountCodeResponse: null,
  partialPaymentsSubtotals: null,
  paymentAmount: 0,
  subtotalsTaxRate: 0,
  taxBreakdowns: []
};
export const SubtotalsContext = /*#__PURE__*/createContext(defaultState);
export const useSubtotals = () => useContext(SubtotalsContext);
function getResultValues(response, lineItems, paymentMethod) {
  const subtotals = getIn(['data', 'subtotals'], response) || getIn(['previousData', 'subtotals'], response) || defaultState;
  return Object.assign({}, subtotals, {
    loading: response.loading,
    error: response.error,
    lineItems,
    paymentMethod
  });
}

// Handles fetching/formatting/returning the correct subtotal preview
// depending on if this app is loaded as a preview iframe during Payment link creation
// or during an actual checkout by a buyer.
export function useSubtotalsContextProvider(checkoutSession) {
  const {
    pathname
  } = useLocation();
  const isIframePreviewUrl = getIsIframePreviewUrl(pathname);
  const currencyCode = checkoutSession.get('currencyCode');
  const lineItems = checkoutSession.get('lineItems').toJS();
  const discount = checkoutSession.get('orderDiscount');
  const adjustments = checkoutSession.get('adjustments');
  const hasCalculateAtCheckoutTaxes = checkoutSession.get('isAutomatedSalesTaxEnabled');
  const {
    iframeableContext
  } = useIframeableContext();
  const embeddedCheckoutFees = iframeableContext && iframeableContext.passthruProps && iframeableContext.passthruProps.checkoutFees;
  const {
    formState
  } = usePaymentFormContext();
  const paymentMethod = formState.get('paymentMethod');
  const subtotalsPreviewResponse = useFetchDynamicSubtotalsPreview({
    checkoutSession,
    portalId: checkoutSession.get('portalId'),
    currencyCode,
    lineItems,
    discount: discount ? discount.toJS() : null,
    adjustments: adjustments ? adjustments.toJS() : [],
    skip: !isIframePreviewUrl,
    paymentMethod,
    checkoutFees: embeddedCheckoutFees
  });
  const subtotalsResponse = useFetchDynamicSubtotals({
    checkoutSession,
    skip: isIframePreviewUrl || hasCalculateAtCheckoutTaxes,
    paymentMethod
  });
  const subtotalsWithTaxResponse = useFetchDynamicSubtotalsWithTax({
    checkoutSession,
    skip: isIframePreviewUrl || !hasCalculateAtCheckoutTaxes,
    paymentMethod
  });
  return useMemo(() => {
    if (isIframePreviewUrl) {
      return getResultValues(subtotalsPreviewResponse, lineItems, paymentMethod);
    } else if (hasCalculateAtCheckoutTaxes) {
      return getResultValues(subtotalsWithTaxResponse, lineItems, paymentMethod);
    }
    return getResultValues(subtotalsResponse, lineItems, paymentMethod);
  }, [subtotalsResponse, subtotalsPreviewResponse, lineItems, isIframePreviewUrl, hasCalculateAtCheckoutTaxes, subtotalsWithTaxResponse, paymentMethod]);
}
export function SubtotalsProvider({
  checkoutSession,
  children
}) {
  const contextValue = useSubtotalsContextProvider(checkoutSession);
  return /*#__PURE__*/_jsx(SubtotalsContext.Provider, {
    value: contextValue,
    children: children
  });
}